import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/layout/Sheet/Sheet";
import { FileText, Info, Plus } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { useChatStore } from "@/store/chat/chat.store";
import type { TCompanyKnowledgeContext } from "@/store/chat/createChatSlice";
import { cn } from "@/utils";
import { useNavigate } from "@tanstack/react-router";
import { useShallow } from "zustand/react/shallow";

type TKnowledgeItem = {
  id: string;
  title: string;
  updated: string;
};

const KNOWLEDGE_ITEMS: TKnowledgeItem[] = [
  { id: "profile", title: "Company profile", updated: "12 days" },
  { id: "storybook", title: "Our company storybook", updated: "12 days" },
  { id: "colors", title: "Brand colors", updated: "12 days" },
  { id: "logo", title: "Logo", updated: "12 days" },
];

type TCompanyKnowledgeDrawerProps = {
  open: boolean;
  context: TCompanyKnowledgeContext | null;
  onClose: () => void;
};

export function CompanyKnowledgeDrawer({ open, onClose, context }: TCompanyKnowledgeDrawerProps) {
  const navigate = useNavigate();
  const { closeChatThreadSession } = useChatStore(
    useShallow((state) => ({
      closeChatThreadSession: state.closeChatThreadSession,
    }))
  );
  // TODO: replace with real capacity data from backend
  const [summaryError, setSummaryError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSummaryError(null);
      return;
    }
    setSummaryError(null);
  }, [open, context?.companyId]);

  const handleOnboardingSummaryClick = () => {
    if (!context) {
      return;
    }

    // Close any active thread session before navigating
    closeChatThreadSession();

    // Navigate to summary page - it handles all states (processing, failed, ready)
    onClose();
    void navigate({
      to: "/app/companies/$company_id/onboarding/summary",
      params: { company_id: context.companyId },
    });
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <SheetContent
        side="right"
        className="w-full max-w-[540px] border-l-0 bg-card p-0 sm:max-w-[560px]"
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b px-8 py-6">
            <SheetHeader className="space-y-1 text-left">
              <SheetTitle className="text-2xl font-semibold tracking-tight">Company Knowledge</SheetTitle>
              <SheetDescription className="text-sm text-muted-foreground">
                Organize docs and guidance so Copilot keeps answers on-brand.
              </SheetDescription>
            </SheetHeader>
            <Button
              size="icon"
              variant="outline"
              aria-label="Add knowledge"
              className="h-10 w-10 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto px-8 py-6">
            <section className="space-y-6">
              <div className="space-y-3 rounded-2xl border border-primary/20 bg-primary/5 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <FileText className="h-5 w-5" />
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-primary">
                      {context?.companyName ?? "Select a company to view the onboarding summary"}
                    </p>
                    <p className="text-xs text-primary/80">
                      Review the answers captured during onboarding to get up to speed instantly.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Button
                    onClick={handleOnboardingSummaryClick}
                    disabled={!context}
                    className="inline-flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    View Onboarding Summary
                  </Button>
                  {!context && (
                    <p className="text-xs text-muted-foreground">
                      Pick a company from the sidebar to enable this action.
                    </p>
                  )}
                </div>
                {summaryError && <p className="text-xs text-destructive">{summaryError}</p>}
              </div>

              <div className="space-y-4 rounded-2xl border border-muted-foreground/10 bg-muted/40 p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    variant="secondary"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium"
                  >
                    Set company instructions
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <Badge
                    variant="outline"
                    className="rounded-full px-3 py-1 text-xs text-muted-foreground"
                  >
                    Optional
                  </Badge>
                </div>

                {/* TODO: restore capacity bar once backend provides real usage data */}
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Knowledge uploads
                </h3>
                <div className="space-y-3">
                  {KNOWLEDGE_ITEMS.map((item) => (
                    <KnowledgeItem
                      key={item.id}
                      item={item}
                    />
                  ))}
                </div>
              </div>
            </section>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

type TKnowledgeItemProps = {
  item: TKnowledgeItem;
};

function KnowledgeItem({ item }: TKnowledgeItemProps) {
  return (
    <button
      type="button"
      className="group flex w-full items-center justify-between rounded-2xl border border-transparent bg-card px-4 py-3 text-left transition-colors hover:border-muted-foreground/20 hover:bg-muted/50"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
          <FileText className="h-5 w-5" />
        </span>
        <span className="flex flex-col">
          <span className="text-sm font-semibold text-foreground">{item.title}</span>
          <span className="text-xs text-muted-foreground">{item.updated}</span>
        </span>
      </div>
      <Badge
        variant="outline"
        className={cn(
          "rounded-full border-primary/40 text-xs font-medium text-primary transition-colors group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground"
        )}
      >
        View
      </Badge>
    </button>
  );
}
